import { useEffect, useState, useRef, useCallback } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const mentorMediaUrl = (p) => `${API}/mentor/media/${p}`;

export function UploadZone({ image, onUploaded, compact = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = useCallback(async (file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "chart.png");
      const r = await fetch(`${API}/mentor/upload`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || "Upload failed");
      onUploaded({ path: d.path, preview: URL.createObjectURL(file) });
      toast.success("Screenshot uploaded");
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  }, [onUploaded]);

  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
      if (item) upload(item.getAsFile());
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [upload]);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
      onClick={() => inputRef.current?.click()}
      className="glass-card border-dashed border-white/15 p-6 sm:p-8 text-center cursor-pointer hover:border-[#9fd3ff]/40 transition-colors"
      data-testid="mentor-upload-zone"
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="mentor-file-input"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      {image ? (
        <div>
          <img src={image.preview || mentorMediaUrl(image.path)} alt="Your chart" className={`${compact ? "max-h-48" : "max-h-72"} mx-auto rounded-lg border border-white/10`} data-testid="mentor-chart-preview" />
          <div className="mt-3 text-[11px] text-zinc-500">Tap to replace · or paste / drop a new screenshot</div>
        </div>
      ) : (
        <div className={compact ? "py-2" : "py-6"}>
          {busy ? <Loader2 className="h-8 w-8 mx-auto text-[#9fd3ff] animate-spin" /> : <ImagePlus className="h-8 w-8 mx-auto text-zinc-500" />}
          <div className="mt-4 text-sm text-zinc-200 font-medium">{busy ? "Uploading…" : "Drop your TradingView screenshot here"}</div>
          <div className="mt-1 text-[12px] text-zinc-500 font-light">Paste from clipboard, drag &amp; drop, or tap to upload from your phone</div>
        </div>
      )}
    </div>
  );
}
